import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { periodCalendarService, Period, PeriodPrediction, PeriodStats } from '../../services/periodCalendarService';
import PageHeader from '../common/PageHeader';
import PeriodFormModal from './PeriodFormModal';
import PeriodCalendarView from './PeriodCalendarView';
import './PeriodCalendar.css';

interface PeriodCalendarProps {
  onToggleSidebar: () => void;
}

const PeriodCalendar: React.FC<PeriodCalendarProps> = ({ onToggleSidebar }) => {
  const { user } = useAuth();
  const [periods, setPeriods] = useState<Period[]>([]);
  const [predictions, setPredictions] = useState<PeriodPrediction[]>([]);
  const [stats, setStats] = useState<PeriodStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState<Period | null>(null);
  const [activeTab, setActiveTab] = useState<'calendar' | 'list'>('calendar');

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    if (!user) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const [periodsRes, predictionsRes, statsRes] = await Promise.all([
        periodCalendarService.getPeriods(user.id, { limit: 100 }),
        periodCalendarService.getPredictions(user.id, 6),
        periodCalendarService.getStats(user.id)
      ]);
      
      if (periodsRes.success && periodsRes.periods) {
        setPeriods(periodsRes.periods);
      }
      
      if (predictionsRes.success && predictionsRes.predictions) {
        setPredictions(predictionsRes.predictions);
      }
      
      if (statsRes.success && statsRes.stats) {
        setStats(statsRes.stats);
      }
    } catch (err) {
      setError('Failed to load period calendar data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (period: Period) => {
    setEditingPeriod(period);
    setShowModal(true);
  };

  const handleModalSave = () => {
    fetchData();
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingPeriod(null);
  };

  const handleModalError = (errorMessage: string) => {
    setError(errorMessage);
    setTimeout(() => setError(null), 5000);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this period entry?')) {
      return;
    }
    
    try {
      await periodCalendarService.deletePeriod(id);
      fetchData();
    } catch (err) {
      setError('Failed to delete period entry');
      console.error(err);
    }
  };

  const handleAddPeriod = () => {
    setEditingPeriod(null);
    setShowModal(true);
  };

  const formatDate = (dateString: string) => {
    // Normalize date string to YYYY-MM-DD format first
    const normalizedDate = normalizeDateString(dateString);
    // Parse YYYY-MM-DD directly to avoid timezone conversion issues
    const [year, month, day] = normalizedDate.split('-').map(Number);
    const date = new Date(Date.UTC(year, month - 1, day));
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
  };

  const normalizeDateString = (dateStr: string): string => {
    // Extract YYYY-MM-DD from ISO string (e.g., "2025-10-20T17:00:00.000Z" -> "2025-10-20")
    // or return as-is if already in YYYY-MM-DD format
    if (dateStr.includes('T')) {
      return dateStr.split('T')[0];
    }
    return dateStr;
  };

  const rightActions = (
    <div className="period-calendar-header-actions">
      <button 
        className="period-calendar-add-btn"
        onClick={handleAddPeriod}
      >
        + Add Period
      </button>
    </div>
  );

  if (loading) {
    return (
      <div className="period-calendar-container">
        <PageHeader
          onToggleSidebar={onToggleSidebar}
          centerContent={<span>/period-calendar</span>}
          rightActions={rightActions}
        />
        <div className="period-calendar-content">
          <div className="period-calendar-loading">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="period-calendar-container">
      <PageHeader
        onToggleSidebar={onToggleSidebar}
        centerContent={<span>/period-calendar</span>}
        rightActions={rightActions}
      />
      
      <div className="period-calendar-content">
        {error && (
          <div className="period-calendar-error">
            {error}
          </div>
        )}

        {showModal && user && (
          <PeriodFormModal
            period={editingPeriod}
            userId={user.id}
            onClose={handleModalClose}
            onSave={handleModalSave}
            onError={handleModalError}
          />
        )}

        {activeTab === 'calendar' && (
          <>
            <div className="period-calendar-calendar-section">
              <h3>Calendar View</h3>
              <PeriodCalendarView 
                periods={periods}
                predictions={predictions}
                stats={stats}
                onDateClick={(date) => {
                  const period = periods.find(p => {
                    const periodStart = normalizeDateString(p.start_date);
                    const periodEnd = p.end_date ? normalizeDateString(p.end_date) : periodStart;
                    return periodStart <= date && periodEnd >= date;
                  });
                  if (period) {
                    handleEdit(period);
                  }
                }}
              />
            </div>

            {stats && (
              <div className="period-calendar-stats-section">
                <h3>Statistics</h3>
                <div className="period-calendar-stats-grid">
                  <div className="period-calendar-stat-item">
                    <span className="period-calendar-stat-label">Total Periods</span>
                    <span className="period-calendar-stat-value">{stats.total_periods}</span>
                  </div>
                  {stats.average_cycle_length && (
                    <div className="period-calendar-stat-item">
                      <span className="period-calendar-stat-label">Avg Cycle Length</span>
                      <span className="period-calendar-stat-value">{stats.average_cycle_length} days</span>
                    </div>
                  )}
                  {stats.average_period_duration && (
                    <div className="period-calendar-stat-item">
                      <span className="period-calendar-stat-label">Avg Duration</span>
                      <span className="period-calendar-stat-value">{stats.average_period_duration} days</span>
                    </div>
                  )}
                  {stats.last_period_start && (
                    <div className="period-calendar-stat-item">
                      <span className="period-calendar-stat-label">Last Period</span>
                      <span className="period-calendar-stat-value">{formatDate(stats.last_period_start)}</span>
                    </div>
                  )}
                  {stats.next_predicted_period && (
                    <div className="period-calendar-stat-item">
                      <span className="period-calendar-stat-label">Next Predicted</span>
                      <span className="period-calendar-stat-value">{formatDate(stats.next_predicted_period)}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {predictions.length > 0 && (
              <div className="period-calendar-predictions-section">
                <h3>Predicted Future Periods</h3>
                <div className="period-calendar-predictions-list">
                  {predictions.map((prediction, index) => (
                    <div key={index} className="period-calendar-prediction-item">
                      <span className="period-calendar-prediction-date">
                        {formatDate(prediction.predicted_start_date)}
                      </span>
                      <span className="period-calendar-prediction-label">
                        Cycle #{prediction.cycle_number}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'list' && (
          <div className="period-calendar-periods-section">
            <h3>Period History</h3>
            {periods.length === 0 ? (
              <div className="period-calendar-empty">No periods recorded yet. Add your first period to get started.</div>
            ) : (
              <div className="period-calendar-periods-list">
                {periods.map((period) => (
                  <div key={period.id} className="period-calendar-period-item">
                    <div className="period-calendar-period-dates">
                      <span className="period-calendar-period-start">
                        {formatDate(period.start_date)}
                      </span>
                      {period.end_date && (
                        <>
                          <span className="period-calendar-period-separator">→</span>
                          <span className="period-calendar-period-end">
                            {formatDate(period.end_date)}
                          </span>
                        </>
                      )}
                    </div>
                    {period.cycle_length && (
                      <div className="period-calendar-period-cycle">
                        Cycle: {period.cycle_length} days
                      </div>
                    )}
                    {period.notes && (
                      <div className="period-calendar-period-notes">
                        {period.notes}
                      </div>
                    )}
                    <div className="period-calendar-period-actions">
                      <button 
                        onClick={() => handleEdit(period)}
                        className="period-calendar-edit-btn"
                      >
                        Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(period.id)}
                        className="period-calendar-delete-btn"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="period-calendar-bottom-tabs">
        <button
          className={`period-calendar-tab ${activeTab === 'calendar' ? 'active' : ''}`}
          onClick={() => setActiveTab('calendar')}
        >
          Calendar
        </button>
        <button
          className={`period-calendar-tab ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          List
        </button>
      </div>
    </div>
  );
};

export default PeriodCalendar;

